/**
 * Meal Log Firestore Service
 * Collection: babies/{babyId}/mealLogs
 * Tracks what baby ate at each meal (breakfast/lunch/dinner/snack) during the solids phase.
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  where,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { MealLog } from '../types';

function mealLogsCol(babyId: string) {
  return collection(db, 'babies', babyId, 'mealLogs');
}

/** Save a new meal log entry to Firestore */
export async function addMealLog(
  babyId: string,
  log: Omit<MealLog, 'id'>
): Promise<MealLog> {
  const ref = await addDoc(mealLogsCol(babyId), {
    ...log,
    timestamp: Timestamp.fromDate(log.timestamp),
    createdAt: serverTimestamp(),
  });
  return { id: ref.id, ...log };
}

/** Fetch all meal logs for a given date (YYYY-MM-DD) */
export async function getMealLogs(
  babyId: string,
  date: string
): Promise<MealLog[]> {
  const snap = await getDocs(
    query(mealLogsCol(babyId), where('date', '==', date), orderBy('timestamp', 'asc'))
  );
  return snap.docs.map(d => ({
    id: d.id,
    ...d.data(),
    timestamp: (d.data().timestamp as Timestamp).toDate(),
  } as MealLog));
}

/** Fetch today's meal logs */
export async function getMealLogsToday(babyId: string): Promise<MealLog[]> {
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return getMealLogs(babyId, dateStr);
}

/**
 * Count unique foods introduced for the first time this week (Monday–today).
 * Uses the solidLogs collection (food introductions), not mealLogs.
 * A food counts as "new this week" if its earliest log falls within this week.
 */
export async function getNewFoodsThisWeek(babyId: string): Promise<number> {
  const { getSolidLogs } = await import('./firestore-baby');
  const allLogs = await getSolidLogs(babyId);

  // Monday of current week
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon...
  const daysFromMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysFromMon);
  weekStart.setHours(0, 0, 0, 0);

  // Find the earliest log date for each foodId
  const firstSeen = new Map<string, Date>();
  for (const log of allLogs) {
    const key = log.foodId || log.foodName;
    const existing = firstSeen.get(key);
    if (!existing || log.timestamp < existing) {
      firstSeen.set(key, log.timestamp);
    }
  }

  // Count foods whose first log is on or after this week's Monday
  let count = 0;
  for (const date of firstSeen.values()) {
    if (date >= weekStart) count++;
  }
  return count;
}
